if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);

        this.sliderInstance = false;

        this.initSwipeDots();
        this.initMobileNav();

        if (!this.enableSlider) return;

        const mql = window.matchMedia(BewoTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
      }

      init() {
        if (BewoTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      initMobileNav() {
        const shell = this.closest('grid-list');
        if (!shell || !this.sliderWrapper) return;

        const prev = shell.querySelector(this.selectors.prevEl);
        const next = shell.querySelector(this.selectors.nextEl);
        if (!prev || !next) return;

        const items = () => Array.from(this.sliderWrapper.children);
        const offsetOf = (item) => {
          const padStart = parseFloat(getComputedStyle(this).paddingInlineStart) || 0;
          return item.getBoundingClientRect().left - this.getBoundingClientRect().left - padStart + this.scrollLeft;
        };
        const currentIndex = () => {
          const list = items();
          if (!list.length) return 0;
          if (this.scrollLeft + this.clientWidth >= this.scrollWidth - 2) return list.length - 1;
          let current = 0;
          let closest = Infinity;
          list.forEach((item, index) => {
            const distance = Math.abs(offsetOf(item) - this.scrollLeft);
            if (distance < closest) {
              closest = distance;
              current = index;
            }
          });
          return current;
        };
        const goTo = (index) => {
          const list = items();
          if (!list.length) return;
          const clamped = Math.max(0, Math.min(index, list.length - 1));
          this.scrollTo({ left: offsetOf(list[clamped]), behavior: 'smooth' });
        };
        const onMobileNav = (direction) => {
          if (!BewoTheme.config.mqlMobile) return false;
          if (this.sliderInstance && typeof this.sliderInstance === 'object') return false;
          goTo(currentIndex() + direction);
          return true;
        };

        prev.addEventListener('click', (event) => {
          if (onMobileNav(-1)) event.preventDefault();
        });
        next.addEventListener('click', (event) => {
          if (onMobileNav(1)) event.preventDefault();
        });
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const columnGap = BewoTheme.utils.getGridColumnGap(this.sliderWrapper);

        this.sliderOptions = {
          slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
          spaceBetween: columnGap.tablet,
          navigation: {
            nextEl: this.section.querySelector(this.selectors.nextEl),
            prevEl: this.section.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          breakpoints: {
            1024: {
              slidesPerView: this.items > 4 ? 4 : parseInt(this.items),
              spaceBetween: columnGap.tabletLarge,
            },
            1280: {
              slidesPerView: parseInt(this.items),
              spaceBetween: columnGap.desktop,
            },
          },
          loop: true,
          threshold: 2,
        };

        this.classList.add(this.classes.swiper, 'swiper-equal-height');
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.BewoTheme.Carousel(this, this.sliderOptions);
        this.sliderInstance.init();

        this.fixQuickviewDuplicate();

        this.calcNavButtonsPosition();

        window.addEventListener("resize", BewoTheme.utils.debounce(() => {
          this.calcNavButtonsPosition();
        }, 100));
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      initSwipeDots() {
        const dots = this.closest('grid-list')?.querySelector('[data-bewo-swipe-dots]');
        if (!dots || !this.sliderWrapper) return;

        const items = Array.from(this.sliderWrapper.children);
        if (items.length < 2) return;

        const offsetOf = (item) => {
          const padStart = parseFloat(getComputedStyle(this).paddingInlineStart) || 0;
          return item.getBoundingClientRect().left - this.getBoundingClientRect().left - padStart + this.scrollLeft;
        };

        dots.innerHTML = '';
        items.forEach((item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'bewo-swipe-dots__dot';
          dot.setAttribute('aria-label', `Ir para o produto ${index + 1} de ${items.length}`);
          dot.addEventListener('click', () => {
            this.scrollTo({ left: offsetOf(item), behavior: 'smooth' });
          });
          dots.appendChild(dot);
        });

        const update = () => {
          let current = 0;
          if (this.scrollLeft + this.clientWidth >= this.scrollWidth - 2) {
            current = items.length - 1;
          } else {
            let closest = Infinity;
            items.forEach((item, index) => {
              const distance = Math.abs(offsetOf(item) - this.scrollLeft);
              if (distance < closest) {
                closest = distance;
                current = index;
              }
            });
          }
          Array.from(dots.children).forEach((dot, index) => {
            dot.setAttribute('aria-current', index === current ? 'true' : 'false');
          });
        };

        let frame = null;
        this.addEventListener(
          'scroll',
          () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
              frame = null;
              update();
            });
          },
          { passive: true }
        );
        update();
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight) / 2 + 'px'
          );
        }
      }

      calcHeadingColumnHeight() {
        if (this.dataset.calcHeadingColumnHeight !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty('--heading-column-height', firstMedia.clientHeight + 'px');
        }
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }
    }
  );
}
